// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import Select, { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { GetAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccount";
import createUser from "@insite/client-framework/Store/Pages/UserList/Handlers/CreateUser";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { PureComponent, ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen?: boolean;
    assignApproverHelpMessage?: string;
    roleInformation?: string;
    onClickCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onClose: (event?: React.SyntheticEvent<Element, Event>) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    currentAccountState: getCurrentAccountState(state),
    showUserName: !getSettingsCollection(state).accountSettings.useEmailAsUserName,
});

const mapDispatchToProps = {
    createUser,
    loadAccount,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface NewUserRoles {
    Value: string;
    DisplayName: string;
}

export interface CreateUserModalStyles {
    modal?: ModalPresentationProps;
    form?: InjectableCss;
    container?: GridContainerProps;
    userNameVisibleGridItem?: GridItemProps;
    userNameHiddenGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    firstNameGridItem?: GridItemProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameTextField?: TextFieldPresentationProps;
    settingsHeadingGridItem?: GridItemProps;
    settingsHeadingText?: TypographyPresentationProps;
    roleGridItem?: GridItemProps;
    roleSelect?: SelectPresentationProps;
    roleSelectHelpIcon?: IconPresentationProps;
    roleSelectHelpModal?: ModalPresentationProps;
    approverGridItem?: GridItemProps;
    approverSelect?: SelectPresentationProps;
    approverSelectTooltip?: TooltipPresentationProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    approverDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const createUserModalStyles: CreateUserModalStyles = {
    modal: {
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
        },
    },
    container: { gap: 15 },
    userNameVisibleGridItem: { width: [12, 12, 12, 6, 6] },
    userNameHiddenGridItem: {
        css: css`
            display: none;
        `,
    },
    emailGridItem: { width: [12, 12, 12, 6, 6] },
    firstNameGridItem: { width: [12, 12, 12, 6, 6] },
    lastNameGridItem: { width: [12, 12, 12, 6, 6] },
    settingsHeadingGridItem: { width: 12 },
    settingsHeadingText: { variant: "h5" },
    roleGridItem: { width: [12, 12, 12, 6, 6] },
    roleSelectHelpIcon: {
        src: HelpCircle,
        size: 18,
        css: css`
            margin-left: 5px;
            cursor: pointer;
        `,
    },
    approverGridItem: { width: [12, 12, 12, 6, 6] },
    approverSelectTooltip: {
        iconProps: {
            css: css`
                margin-left: 5px;
            `,
        },
    },
    buttonsGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: 12,
    },
    cancelButton: { variant: "secondary" },
    submitButton: {
        css: css`
            margin-left: 1rem;
        `,
    },
};

const styles = createUserModalStyles;

const StyledForm = getStyledWrapper("form");

class CreateUserModal extends PureComponent<
    Props,
    {
        userName: string;
        email: string;
        firstName: string;
        lastName: string;
        role: string;
        approver: string;
        requiresActivation: boolean;
        userNameErrorMessage?: ReactNode;
        emailErrorMessage?: ReactNode;
        firstNameErrorMessage?: ReactNode;
        lastNameErrorMessage?: ReactNode;
        approverErrorMessage?: ReactNode;
        showFormErrors: boolean;
        otherErrors: string;
        roleInformationModalIsOpen: boolean;
        approvers: OptionObject[];
    }
> {
    state = {
        userName: "",
        email: "",
        firstName: "",
        lastName: "",
        role: "",
        approver: "",
        requiresActivation: false,
        userNameErrorMessage: undefined,
        emailErrorMessage: undefined,
        firstNameErrorMessage: undefined,
        lastNameErrorMessage: undefined,
        approverErrorMessage: undefined,
        showFormErrors: false,
        otherErrors: "",
        roleInformationModalIsOpen: false,
        approvers: [],
    };

    usernameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ userName: event.target.value }, () => {
            this.state.showFormErrors && this.validateUserName();
        });
    };

    emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ email: event.target.value }, () => {
            this.state.showFormErrors && this.validateEmail();
        });
    };

    firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ firstName: event.target.value }, () => {
            this.state.showFormErrors && this.validateFirstName();
        });
    };

    lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ lastName: event.target.value }, () => {
            this.state.showFormErrors && this.validateLastName();
        });
    };

    sendActivationEmailChangeHandler = (event: React.SyntheticEvent, value: boolean) => {
        this.setState({ requiresActivation: value });
    };

    roleChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ role: event.target.value }, () => {
            this.state.showFormErrors && this.validateApprover();
        });
    };

    approverChangeHandler = (value?: string) => {
        this.setState({ approver: value || "" }, () => {
            this.state.showFormErrors && this.validateApprover();
        });
    };

    validateUserName = () => {
        const userNameErrorMessage = !this.state.userName ? siteMessage("CreateNewAccountInfo_UserName_Required") : "";

        this.setState({ userNameErrorMessage });

        return !userNameErrorMessage;
    };

    validateEmail = () => {
        let emailErrorMessage: ReturnType<typeof siteMessage> = "";

        if (!this.state.email) {
            emailErrorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_Required");
        } else if (!validateEmail(this.state.email)) {
            emailErrorMessage = siteMessage("CreateNewAccountInfo_EmailAddress_ValidEmail");
        }

        this.setState({ emailErrorMessage });

        return !emailErrorMessage;
    };

    validateFirstName = () => {
        const firstNameErrorMessage = !this.state.firstName ? siteMessage("User_Admin_Info_FirstName_Required") : "";

        this.setState({ firstNameErrorMessage });

        return !firstNameErrorMessage;
    };

    validateLastName = () => {
        const lastNameErrorMessage = !this.state.lastName ? siteMessage("User_Admin_Info_LastName_Required") : "";

        this.setState({ lastNameErrorMessage });

        return !lastNameErrorMessage;
    };

    validateApprover = () => {
        const approverErrorMessage =
            ["Buyer1", "Requisitioner"].indexOf(this.state.role) > -1 && !this.state.approver
                ? siteMessage("CreateNewAccountInfo_Approver_Required")
                : "";

        this.setState({ approverErrorMessage });

        return !approverErrorMessage;
    };

    validateForm = () => {
        const isEmailValid = this.validateEmail();
        const isFirstNameValid = this.validateFirstName();
        const isLastNameValid = this.validateLastName();
        const isValidApprover = this.validateApprover();

        let isFormValid = isEmailValid && isFirstNameValid && isLastNameValid && isValidApprover;

        if (this.props.showUserName) {
            isFormValid = this.validateUserName() && isFormValid;
        }

        return isFormValid;
    };

    formSubmitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.setState({ otherErrors: "" });

        if (!this.validateForm()) {
            this.setState({ showFormErrors: true });

            return false;
        }

        await this.props.createUser({
            userName: this.state.userName,
            email: this.state.email,
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            role: this.state.role,
            requiresActivation: this.state.requiresActivation,
            approver: this.state.approver,
            onError: (errorMessage: string) => {
                let errorMsg;

                try {
                    errorMsg = JSON.parse(errorMessage)?.Message;
                } catch (e) {
                    errorMsg = errorMessage;
                }

                this.setState({
                    otherErrors: errorMsg,
                });
            },
            onSuccess: (result?: any) => {
                this.props.toaster.addToast({
                    body: this.state.requiresActivation
                        ? translate("User Created. An activation email has been sent.")
                        : translate("User Created."),
                    messageType: "success",
                });

                this.props.onClose();
                this.resetState();
            },
        } as any);
    };

    resetState = () => {
        this.setState({
            userName: "",
            email: "",
            firstName: "",
            lastName: "",
            role: "",
            approver: "",
            requiresActivation: false,
            userNameErrorMessage: undefined,
            emailErrorMessage: undefined,
            firstNameErrorMessage: undefined,
            lastNameErrorMessage: undefined,
            approverErrorMessage: undefined,
            showFormErrors: false,
            otherErrors: "",
        });
    };

    closeModalHandler = (event: React.SyntheticEvent<Element, Event>) => {
        this.props.onClose(event);
        this.resetState();
    };

    cancelFormHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        this.props.onClickCancel(event);
        this.resetState();
    };
    onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.debouncedSearchApprovers(event.target.value);
    };

    debouncedSearchApprovers = debounce((filterValue: string) => {
        this.setApproverOptions(filterValue);
    }, 300);

    componentDidUpdate() {
        if (this.state.approvers.length === 0) {
            this.setApproverOptions("");
        }
    }

    setApproverOptions = (filterValue: string) => {
        const currentAccountId = this.props.currentAccountState.value?.id;
        if (!currentAccountId) {
            return;
        }

        let approverOptions: OptionObject[] = [{ optionText: translate("Select an Approver"), optionValue: "" }];
        const parameter = {
            accountId: currentAccountId,
            numberOfApprovers: 20,
        } as GetAccountApiParameter;

        if (filterValue !== "") {
            parameter.approversUsernameFilter = filterValue;
        }

        this.props.loadAccount({
            ...parameter,

            onComplete: result => {
                if (!result?.apiResult?.availableApprovers) {
                    return;
                }

                approverOptions = approverOptions.concat(
                    result.apiResult.availableApprovers.map(o => ({ optionText: o, optionValue: o })),
                );

                this.setState({ approvers: approverOptions });
            },
        });
    };

    render() {
        let userNameGridItemStyles = styles.userNameHiddenGridItem;
        let availableRoles: NewUserRoles[] = [];
        let availableApprovers: string[] | null = [];

        const {
            userName,
            email,
            firstName,
            lastName,
            role,
            approver,
            showFormErrors,
            requiresActivation,
            otherErrors,
        } = this.state;

        if (this.props.showUserName) {
            userNameGridItemStyles = styles.userNameVisibleGridItem;
        }

        if (this.props.currentAccountState.value) {
            try {
                availableRoles = JSON.parse(this.props.currentAccountState.value.properties["availableRoles"]);
            } catch (e) {
                availableRoles = [];
            }

            availableApprovers = this.props.currentAccountState.value.availableApprovers;
        }

        const roleSelectLabel = this.props.roleInformation ? (
            <>
                {translate("Assign User Role")}
                <Icon
                    {...styles.roleSelectHelpIcon}
                    onClick={() => this.setState({ roleInformationModalIsOpen: true })}
                />
                <Modal
                    {...styles.roleSelectHelpModal}
                    headline={translate("User Roles")}
                    isOpen={this.state.roleInformationModalIsOpen}
                    handleClose={() => this.setState({ roleInformationModalIsOpen: false })}
                >
                    {parse(this.props.roleInformation, parserOptions)}
                </Modal>
            </>
        ) : (
            translate("Assign User Role")
        );

        const approverLabel = this.props.assignApproverHelpMessage ? (
            <>
                {translate("Assign Approver")}
                <Tooltip {...styles.approverSelectTooltip} text={this.props.assignApproverHelpMessage} />
            </>
        ) : (
            translate("Assign Approver")
        );

        return (
            <Modal
                {...styles.modal}
                handleClose={this.closeModalHandler}
                headline={translate("Create User")}
                sizeVariant="medium"
                isOpen={this.props.isOpen}
                data-test-selector="createUserModal"
            >
                <StyledForm {...styles.form} noValidate onSubmit={this.formSubmitHandler}>
                    {otherErrors && (
                        <Notify
                            messageType="danger"
                            notifyContainerOverrides={css`
                                max-width: 100%;
                                margin-bottom: 15px;
                            `}
                            body={otherErrors}
                        />
                    )}
                    <GridContainer {...styles.container}>
                        {this.props.showUserName && (
                            <GridItem {...userNameGridItemStyles}>
                                <TextField
                                    {...styles.userNameTextField}
                                    error={showFormErrors && this.state.userNameErrorMessage}
                                    label={translate("Username")}
                                    onChange={this.usernameChangeHandler}
                                    required
                                    value={userName}
                                    data-test-selector="createUserModal_userNameText"
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.emailGridItem}>
                            <TextField
                                {...styles.emailTextField}
                                error={showFormErrors && this.state.emailErrorMessage}
                                label={translate("Email")}
                                onChange={this.emailChangeHandler}
                                required
                                maxLength={256}
                                minLength={5}
                                value={email}
                                data-test-selector="createUserModal_emailText"
                            />
                        </GridItem>
                        <GridItem {...styles.firstNameGridItem}>
                            <TextField
                                {...styles.firstNameTextField}
                                error={showFormErrors && this.state.firstNameErrorMessage}
                                label={translate("First Name")}
                                onChange={this.firstNameChangeHandler}
                                required
                                maxLength={25}
                                minLength={1}
                                value={firstName}
                                data-test-selector="createUserModal_firstNameText"
                            />
                        </GridItem>
                        <GridItem {...styles.lastNameGridItem}>
                            <TextField
                                {...styles.lastNameTextField}
                                error={showFormErrors && this.state.lastNameErrorMessage}
                                label={translate("Last Name")}
                                onChange={this.lastNameChangeHandler}
                                required
                                maxLength={25}
                                minLength={1}
                                value={lastName}
                                data-test-selector="createUserModal_lastNameText"
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox checked={requiresActivation} onChange={this.sendActivationEmailChangeHandler}>
                                {translate("Send Activation Email")}
                            </Checkbox>
                        </GridItem>
                        <GridItem {...styles.settingsHeadingGridItem}>
                            <Typography {...styles.settingsHeadingText} as="h2">
                                {translate("Settings")}
                            </Typography>
                        </GridItem>
                        {availableRoles.length > 0 && (
                            <GridItem {...styles.roleGridItem}>
                                <Select
                                    {...styles.roleSelect}
                                    label={roleSelectLabel}
                                    onChange={this.roleChangeHandler}
                                    value={role}
                                    data-test-selector="createUserModal_userRoleSelect"
                                >
                                    <option value="">{translate("Select User Role")}</option>
                                    {availableRoles.map(o => (
                                        <option key={o.Value} value={o.Value}>
                                            {o.DisplayName}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {availableApprovers && (
                            <GridItem {...styles.approverGridItem}>
                                <DynamicDropdown
                                    label={approverLabel}
                                    {...styles.approverDynamicDropdown}
                                    isLoading={this.props.currentAccountState.isLoading}
                                    onSelectionChange={this.approverChangeHandler}
                                    onInputChange={this.onInputChanged}
                                    selected={approver}
                                    options={this.state.approvers}
                                    data-test-selector="createUserModal_approverSelect"
                                    placeholder={translate("Select an Approver")}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.buttonsGridItem}>
                            <Button
                                {...styles.cancelButton}
                                data-test-selector="createUserModal_cancelButton"
                                onClick={this.cancelFormHandler}
                                type="button"
                            >
                                {translate("Cancel")}
                            </Button>
                            <Button
                                {...styles.submitButton}
                                data-test-selector="createUserModal_createUserButton"
                                type="submit"
                            >
                                {translate("Create User")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </Modal>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(CreateUserModal));
